import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PayrollTable = ({ employees, onEmployeeUpdate, onBulkProcess, selectedEmployees, onEmployeeSelect, onEditPayroll, onViewEarnings, onViewPayments, onViewDetailedPayslip, hasRestrictedSelection }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterSalaryRange, setFilterSalaryRange] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showValidationOnly, setShowValidationOnly] = useState(false);

  const departmentOptions = [
    { value: '', label: 'All Departments' },
    { value: 'security', label: 'Security' },
    { value: 'housekeeping', label: 'Housekeeping' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'administration', label: 'Administration' }
  ];

  const salaryRangeOptions = [
    { value: '', label: 'All Ranges' },
    { value: '0-15000', label: '₹0 - ₹15,000' },
    { value: '15000-25000', label: '₹15,000 - ₹25,000' },
    { value: '25000-40000', label: '₹25,000 - ₹40,000' },
    { value: '40000+', label: '₹40,000+' }
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredEmployees = employees.filter(employee => {
    const name = (employee.name || '').toLowerCase();
    const empId = (employee.employeeId || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = name.includes(search) || empId.includes(search);
    const matchesDepartment = !filterDepartment || (employee.department || '').toLowerCase() === filterDepartment.toLowerCase();
    const matchesValidation = !showValidationOnly || employee.hasValidationIssues;
    
    let matchesSalaryRange = true;
    if (filterSalaryRange) {
      const netPay = employee.netPay;
      switch (filterSalaryRange) {
        case '0-15000':
          matchesSalaryRange = netPay >= 0 && netPay <= 15000;
          break;
        case '15000-25000':
          matchesSalaryRange = netPay > 15000 && netPay <= 25000;
          break;
        case '25000-40000':
          matchesSalaryRange = netPay > 25000 && netPay <= 40000;
          break;
        case '40000+':
          matchesSalaryRange = netPay > 40000;
          break;
      }
    }
    
    return matchesSearch && matchesDepartment && matchesSalaryRange && matchesValidation;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalNetPay = filteredEmployees.reduce((sum, emp) => sum + (emp.netPay || 0), 0);
  const totalGrossPay = filteredEmployees.reduce((sum, emp) => sum + (emp.grossSalary || emp.basicSalary + emp.allowances || 0), 0);
  const totalDeductions = filteredEmployees.reduce((sum, emp) => {
    return sum + (emp.pf || 0) + (emp.esi || 0) + (emp.pt || 0) + (emp.tds || 0) + (emp.medicalInsurance || 0);
  }, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'locked':
        return 'text-success bg-success/10 border-success/20';
      case 'approved':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'reviewed':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'draft':
        return 'text-muted-foreground bg-muted border-border';
      case 'processed':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Draft';
    return status
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const isRestrictedEmployee = (employee) => {
    return ['inactive', 'suspended'].includes(employee?.employmentStatus);
  };

  const getNextWorkflowAction = (status) => {
    switch (status) {
      case 'draft':
        return { label: 'Mark Reviewed', nextStatus: 'reviewed' };
      case 'reviewed':
        return { label: 'Approve', nextStatus: 'approved' };
      case 'approved':
        return { label: 'Lock', nextStatus: 'locked' };
      default:
        return null;
    }
  };

  const handleMissingPayrollRecord = (employee) => {
    if (onEditPayroll) {
      onEditPayroll(employee);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filters and Search */}
      {hasRestrictedSelection && (
        <div className="p-4 bg-amber-50 border border-amber-200 text-amber-900">
          Some selected employees are inactive or suspended. Payroll actions are blocked until restricted items are deselected.
        </div>
      )}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={20} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Employee Payroll</h3>
              <p className="text-sm text-muted-foreground">{filteredEmployees.length} employees</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onBulkProcess(selectedEmployees)}
              disabled={selectedEmployees.length === 0 || hasRestrictedSelection}
            >
              <Icon name="Play" size={16} className="mr-2" />
              Process Selected ({selectedEmployees.length})
            </Button>
            <Button variant="default" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-full">
          <Input
            type="search"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <Select
            options={departmentOptions}
            value={filterDepartment}
            onChange={setFilterDepartment}
            placeholder="Filter by department"
          />
          
          <Select
            options={salaryRangeOptions}
            value={filterSalaryRange}
            onChange={setFilterSalaryRange}
            placeholder="Filter by salary range"
          />

          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="rounded border-border"
              checked={showValidationOnly}
              onChange={(e) => setShowValidationOnly(e.target.checked)}
            />
            Show validation issues only
          </label>
          
          <Button variant="outline" onClick={() => {
            setSearchTerm('');
            setFilterDepartment('');
            setFilterSalaryRange('');
            setShowValidationOnly(false);
          }}>
            <Icon name="X" size={16} className="mr-2" />
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Total Gross Pay</p>
                <p className="text-xl font-semibold text-foreground">{formatCurrency(totalGrossPay)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="TrendingDown" size={20} className="text-error" />
              <div>
                <p className="text-sm text-muted-foreground">Total Deductions</p>
                <p className="text-xl font-semibold text-foreground">{formatCurrency(totalDeductions)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Icon name="DollarSign" size={20} className="text-success" />
              <div>
                <p className="text-sm text-muted-foreground">Total Net Pay</p>
                <p className="text-xl font-semibold text-foreground">{formatCurrency(totalNetPay)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredEmployees.length === 0 ? (
        <div className="p-12 text-center">
          <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Payroll Records Found</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm || filterDepartment || filterSalaryRange
              ? 'No employees match your current filters. Try adjusting your search criteria.'
              : 'No payroll records found for the selected period. Create a payroll record to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-6">
          <div className="inline-block min-w-full align-middle px-6">
            <table className="w-full table-auto min-w-max">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-2 w-12">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  onChange={(e) => {
                    if (e.target.checked) {
                      onEmployeeSelect(sortedEmployees.filter(emp => !isRestrictedEmployee(emp)).map(emp => emp.id));
                    } else {
                      onEmployeeSelect([]);
                    }
                  }}
                  title="Select all active payroll employees"
                />
              </th>
              <th className="text-left p-2 min-w-[150px] cursor-pointer hover:bg-muted/70" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">Employee</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="text-left p-2 min-w-[120px]">
                <span className="text-xs font-medium">Client Site</span>
              </th>
              <th className="text-left p-2 min-w-[100px] cursor-pointer hover:bg-muted/70" onClick={() => handleSort('basicSalary')}>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">Basic</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="text-left p-2 min-w-[90px]">
                <span className="text-xs font-medium">Allowances</span>
              </th>
              <th className="text-left p-2 min-w-[70px]">
                <span className="text-xs font-medium">PF</span>
              </th>
              <th className="text-left p-2 min-w-[70px]">
                <span className="text-xs font-medium">ESI</span>
              </th>
              <th className="text-left p-2 min-w-[70px]">
                <span className="text-xs font-medium">PT</span>
              </th>
              <th className="text-left p-2 min-w-[70px]">
                <span className="text-xs font-medium">TDS</span>
              </th>
              <th className="text-left p-2 min-w-[100px] cursor-pointer hover:bg-muted/70" onClick={() => handleSort('netPay')}>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium">Net Pay</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="text-left p-2 min-w-[80px]">
                <span className="text-xs font-medium">Status</span>
              </th>
              <th className="text-left p-2 min-w-[140px]">
                <span className="text-xs font-medium">Validation</span>
              </th>
              <th className="text-left p-2 min-w-[140px]">
                <span className="text-xs font-medium">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map((employee) => (
              <tr key={employee.id} className="border-t border-border hover:bg-muted/30">
                <td className="p-4">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={selectedEmployees.includes(employee.id)}
                    disabled={isRestrictedEmployee(employee)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onEmployeeSelect([...selectedEmployees, employee.id]);
                      } else {
                        onEmployeeSelect(selectedEmployees.filter(id => id !== employee.id));
                      }
                    }}
                    title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot be selected for payroll' : ''}
                  />
                </td>
                <td className="p-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={14} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm truncate max-w-[120px]">{employee.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground truncate max-w-[120px]">{employee.employeeId}</p>
                        {employee.identifierMismatch && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            ID mismatch
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-2 whitespace-nowrap">
                  {employee.clientSiteName ? (
                    <div className="max-w-[120px]">
                      <p className="text-xs font-medium text-foreground truncate">{employee.clientSiteName}</p>
                      {employee.clientName && (
                        <p className="text-xs text-muted-foreground truncate">{employee.clientName}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Not assigned</span>
                  )}
                </td>
                <td className="p-2 font-medium text-sm whitespace-nowrap">
                  {formatCurrency(employee.basicSalary || 0)}
                  {employee.isProjected && (
                    <span className="ml-1 text-[10px] text-muted-foreground" title="Estimated from offer CTC">~</span>
                  )}
                </td>
                <td className="p-2 text-sm whitespace-nowrap">{formatCurrency(employee.allowances || 0)}</td>
                <td className="p-2 text-error text-sm whitespace-nowrap">-{formatCurrency(employee.pf || 0)}</td>
                <td className="p-2 text-error text-sm whitespace-nowrap">-{formatCurrency(employee.esi || 0)}</td>
                <td className="p-2 text-error text-sm whitespace-nowrap">-{formatCurrency(employee.pt || 0)}</td>
                <td className="p-2 text-error text-sm whitespace-nowrap">-{formatCurrency(employee.tds || 0)}</td>
                <td className="p-2 font-semibold text-success text-sm whitespace-nowrap">{formatCurrency(employee.netPay || 0)}</td>
                <td className="p-2 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${getStatusColor(employee.status)}`}>
                    {formatStatus(employee.status)}
                  </span>
                </td>
                <td className="p-2 whitespace-nowrap">
                  {employee.hasValidationIssues ? (
                    <div className="flex flex-wrap gap-1">
                      {employee.validationIssues.map((issue) => (
                        <span
                          key={issue}
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                          title={issue}
                        >
                          {issue.replace('Missing ', '')}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-success font-medium">Validated</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    {(() => {
                      const action = getNextWorkflowAction(employee.status);
                      if (!action) {
                        return null;
                      }
                      if (employee.status === 'reviewed') {
                        return null;
                      }
                      return (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            if (employee._originalPayrollRecord && onEmployeeUpdate) {
                              onEmployeeUpdate(employee._originalPayrollRecord.id, { status: action.nextStatus }, employee);
                            } else {
                              handleMissingPayrollRecord(employee);
                            }
                          }}
                          disabled={employee.hasValidationIssues || isRestrictedEmployee(employee)}
                          title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot be updated' : (employee.hasValidationIssues ? 'Resolve validation issues first' : action.label)}
                        >
                          <Icon name="ArrowRight" size={16} />
                        </Button>
                      );
                    })()}
                    {employee.status === 'reviewed' && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (employee._originalPayrollRecord && onEmployeeUpdate) {
                            onEmployeeUpdate(employee._originalPayrollRecord.id, { status: 'approved' }, employee);
                          } else {
                            handleMissingPayrollRecord(employee);
                          }
                        }}
                        disabled={employee.hasValidationIssues || isRestrictedEmployee(employee)}
                        title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot be approved' : (employee.hasValidationIssues ? 'Resolve validation issues first' : 'Approve payroll')}
                      >
                        <Icon name="Check" size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (employee._originalPayrollRecord && onViewDetailedPayslip) {
                          onViewDetailedPayslip(employee);
                        } else {
                          handleMissingPayrollRecord(employee);
                        }
                      }}
                      disabled={isRestrictedEmployee(employee)}
                      title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot view payslip' : (employee._originalPayrollRecord ? 'View Detailed Payslip' : 'Create Payroll Record')}
                    >
                      <Icon name="FileText" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (employee._originalPayrollRecord && onViewEarnings) {
                          onViewEarnings(employee);
                        } else {
                          handleMissingPayrollRecord(employee);
                        }
                      }}
                      disabled={isRestrictedEmployee(employee)}
                      title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot view earnings' : (employee._originalPayrollRecord ? 'View Earnings Breakdown' : 'Create Payroll Record')}
                    >
                      <Icon name="TrendingUp" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (employee._originalPayrollRecord && onViewPayments) {
                          onViewPayments(employee);
                        } else {
                          handleMissingPayrollRecord(employee);
                        }
                      }}
                      disabled={isRestrictedEmployee(employee)}
                      title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot view payments' : (employee._originalPayrollRecord ? 'View Payment History' : 'Create Payroll Record')}
                    >
                      <Icon name="CreditCard" size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      title={isRestrictedEmployee(employee) ? 'Inactive or suspended employees cannot be edited' : 'Edit Payroll'}
                      onClick={() => {
                        if (onEditPayroll && !isRestrictedEmployee(employee)) {
                          onEditPayroll(employee);
                        }
                      }}
                      disabled={isRestrictedEmployee(employee)}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollTable;